import { TwitchatDataTypes } from "@/types/TwitchatDataTypes";
import * as Sentry from "@sentry/vue";
import { toRaw } from "vue";
import StoreProxy from "./StoreProxy";

type EmojiShortcodeEntry = TwitchatDataTypes.Emoji;

/**
 * Created : 28/07/2023
 */
export default class Database {
	public static MESSAGES_TABLE: string = "messages";
	public static GROQ_HISTORY_TABLE: string = "groqHistory";
	public static EMOJI_SHORTCODES_TABLE: string = "emojiShortcodes";

	private static _instance: Database;

	private DB_VERSION: number = 9;

	private _dbConnection!: IDBOpenDBRequest;
	private _messageStore!: IDBObjectStore;
	private _groqStore!: IDBObjectStore;
	private _db!: IDBDatabase;
	private _cleanTimeout: number = -1;
	private _maxMessageRecords: number = 20000;
	private _maxGroqRecords: number = 500;
	private _ready: boolean = true;
	private _quotaWarned: boolean = false;
	private _versionUpgraded: boolean = false;
	private _updateDebounceDelay: number = 500;
	private _updateMaxDelay: number = 5000;
	private _pendingUpdates: Map<string, PendingMessageUpdate> = new Map();
	private _unloadHandlerSet: boolean = false;

	constructor() {}

	/********************
	 * GETTER / SETTERS *
	 ********************/
	static get instance(): Database {
		if (!Database._instance) {
			Database._instance = new Database();
		}
		return Database._instance;
	}

	/******************
	 * PUBLIC METHODS *
	 ******************/
	public async connect(): Promise<void> {
		if (this._db) return Promise.resolve();

		// Force save on page unload
		if (!this._unloadHandlerSet) {
			this._unloadHandlerSet = true;
			window.addEventListener("pagehide", () => this.flushMessageUpdates());
		}

		return new Promise((resolve, _reject) => {
			this._dbConnection = window.indexedDB.open("Twitchat", this.DB_VERSION);
			this._dbConnection.onerror = (event) => {
				console.log(event);
				StoreProxy.common.alert(
					"[IndexedDB] An error occurred when connecting to local database: " +
						((event.target as any)?.errorCode || (event.target as any)?.error?.message),
				);
			};
			this._dbConnection.onsuccess = async (event) => {
				if (event.type != "success") {
					StoreProxy.common.alert(
						"[IndexedDB] An error occurred when connecting to local database: " +
							((event.target as any)?.errorCode || event.type),
					);
					return;
				}
				this._db = (event.target as any)?.result;
				this._db.onclose = (e) => {
					this._ready = false;
					console.log("[DB] close event", e);
				};
				this._db.onabort = (e) => {
					this._ready = false;
					console.log("[DB] abort event", e);
				};
				this._db.onerror = (e) => {
					this._ready = false;
					console.log("[DB] error event", e);
				};
				if (this._versionUpgraded) {
					// await this.clearMessages();
					// await this.clearGroqHistory();
				} else {
					try {
						//These should remain within the onupgradeneeded() callback but for some
						//reason it sometimes failed to create a new table after a version upgrade
						//while still upgrading the DB version.
						//This stuck the users with a missing table.
						//So as a fallback, we create tables here if they don't exist.
						// [EDIT] IndexedDB doesn't allow to create tables outside of onupgradeneeded
						// await this.createMessageTable();
						// await this.createGroqTable();

						this.limitMessageCount();
						this.limitGroqCount();
					} catch (error) {
						const tables = Array.from(this._db.objectStoreNames);
						const version = this._db.version;
						// Avoid blocking app start if an error occurs.
						// this would lead to requesting user to authenticated back
						console.error(error);
						Sentry.captureException(error);
						Sentry.captureMessage(
							"Database init error " +
								(error as Error).message +
								". Available tables: " +
								tables.join(", ") +
								". DB version: " +
								version,
						);
					}
				}
				resolve();
			};
			this._dbConnection.onupgradeneeded = (event) => {
				this._ready = false;
				this._db = (event.target as any)?.result;
				this._db.onclose = (e) => {
					this._ready = false;
					console.log("[DB] close event", e);
				};
				this._db.onabort = (e) => {
					this._ready = false;
					console.log("[DB] abort event", e);
				};
				this._db.onerror = (e) => {
					this._ready = false;
					console.log("[DB] error event", e);
				};
				this.createMessageTable();
				this.createGroqTable();
				this.createEmojiShortcodesTable();
				this._versionUpgraded = (event.newVersion || 0) > event.oldVersion;
				this._ready = true;
			};
		});
	}

	/**
	 * Get all chat messages from the DB
	 */
	public async getMessageList(): Promise<TwitchatDataTypes.ChatMessageTypes[]> {
		if (!this._db || !this._ready) return [];
		return new Promise((resolve, _reject) => {
			const query = this._db
				.transaction(Database.MESSAGES_TABLE, "readonly")
				.objectStore(Database.MESSAGES_TABLE)
				.getAll();
			query.addEventListener("success", (event) => {
				const result =
					((event.target as IDBRequest).result as TwitchatDataTypes.ChatMessageTypes[]) ||
					[];
				resolve(result);
			});
			query.addEventListener("error", (event) => {
				console.error("Get message list error");
				console.error(event);
				resolve([]);
			});
		});
	}

	/**
	 * Add a chat message to the DB
	 *
	 * @param message
	 */
	public async addMessage(message: TwitchatDataTypes.ChatMessageTypes): Promise<void> {
		if (!this._db || !this._ready) return Promise.reject("Database not ready");
		message = toRaw(message);
		const sAuth = StoreProxy.auth;
		const sourceChanId = message.twitchSharedChatSourceId || message.channel_id;
		const isFromRemoteChan =
			sourceChanId != sAuth.twitch.user.id &&
			sourceChanId != sAuth.youtube?.user.id &&
			sourceChanId != sAuth.bluesky?.user.id;
		//Don't save messages from remote channels
		if (isFromRemoteChan) return Promise.resolve();

		const ignoreList: TwitchatDataTypes.TwitchatMessageStringType[] = [
			TwitchatDataTypes.TwitchatMessageType.JOIN,
			TwitchatDataTypes.TwitchatMessageType.LEAVE,
			TwitchatDataTypes.TwitchatMessageType.CONNECT,
			TwitchatDataTypes.TwitchatMessageType.DISCONNECT,
			TwitchatDataTypes.TwitchatMessageType.TWITCHAT_AD,
			TwitchatDataTypes.TwitchatMessageType.SCOPE_REQUEST,
			TwitchatDataTypes.TwitchatMessageType.ROOM_SETTINGS,
			TwitchatDataTypes.TwitchatMessageType.HISTORY_SPLITTER,
		];
		//Do not save above message types. They're useless to recover
		if (ignoreList.includes(message.type)) return;
		if (
			message.type == TwitchatDataTypes.TwitchatMessageType.NOTICE &&
			message.noticeId == "devMode"
		)
			return;

		return new Promise((resolve, reject) => {
			const { data, json } = this.removeCircularReferences(message);

			//Dirty way to check if a user is pending for login or display name
			//to be loaded. Wait an arbitrary duration to give it some time to
			//load and update the message on the database.
			//Far from ideal and clean solution i know...
			//This "fixes" issues when receiving a user event but this user's
			//profile have not been loaded/filled yet. The message is directly
			//saved on the DB but as the user info are not yet loaded we end up
			//with "...loading..." as usernames stored on DB.
			//These temporary usernames then show up on the history after
			//reloading twitchat.
			//For example this happens when receiving a whisper from someone who's profile
			//has not been loaded before.
			if (json.indexOf(StoreProxy.users.tmpDisplayName) > -1) {
				window.setTimeout(() => {
					this.updateMessage(message);
				}, 5000);
			}

			const query = this._db
				.transaction(Database.MESSAGES_TABLE, "readwrite")
				.objectStore(Database.MESSAGES_TABLE)
				.add(data);
			query.addEventListener("success", (event) => {
				clearTimeout(this._cleanTimeout);
				this._cleanTimeout = window.setTimeout(() => {
					if ((event.target as IDBRequest).result > this._maxMessageRecords) {
						this.limitMessageCount();
					}
				}, 1000);
				resolve();
			});
			query.addEventListener("error", (event) => {
				console.error("Add message error");
				console.log(message);
				console.error(event);
				reject("[Database] Add message error");
			});
			query.addEventListener("onabort", (event) => {
				const error = (event.target as IDBRequest).error;
				if (error && error.name == "QuotaExceededError" && !this._quotaWarned) {
					this._quotaWarned = true;
					StoreProxy.common.alert(
						"[IndexedDB] Storage quota reached, cannot save new message in history",
					);
					this.limitMessageCount();
				}
				resolve();
			});
		});
	}

	/**
	 * Updates a message on the DB.
	 *
	 * Writes are debounced per message ID and only keep latest update.
	 * @param message
	 */
	public async updateMessage(message: TwitchatDataTypes.ChatMessageTypes): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();
		const sAuth = StoreProxy.auth;
		const isFromRemoteChan =
			message.channel_id != sAuth.twitch.user.id &&
			message.channel_id != sAuth.youtube?.user.id;
		//Don't save messages from remote channels
		if (isFromRemoteChan) return Promise.resolve();

		return new Promise((resolve, _reject) => {
			const id = message.id;
			const pending = this._pendingUpdates.get(id);
			if (pending) {
				pending.message = message;
				pending.resolvers.push(resolve);
				// Just a fail safe in case a message gets constantly update to at least
				// write it a few seconds after the first write attempt
				if (Date.now() - pending.firstRequestDate >= this._updateMaxDelay) {
					this.flushMessageUpdate(id);
					return;
				}
				clearTimeout(pending.timeout);
				pending.timeout = window.setTimeout(
					() => this.flushMessageUpdate(id),
					this._updateDebounceDelay,
				);
				return;
			}
			this._pendingUpdates.set(id, {
				message,
				resolvers: [resolve],
				firstRequestDate: Date.now(),
				timeout: window.setTimeout(
					() => this.flushMessageUpdate(id),
					this._updateDebounceDelay,
				),
			});
		});
	}

	/**
	 * Force write of any pending updates
	 */
	public flushMessageUpdates(): void {
		for (const id of this._pendingUpdates.keys()) {
			this.flushMessageUpdate(id);
		}
	}

	/**
	 * Deletes a message from the DB
	 * @param message
	 */
	public async deleteMessage(message: TwitchatDataTypes.ChatMessageTypes): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();
		const sAuth = StoreProxy.auth;
		const isFromRemoteChan =
			message.channel_id != sAuth.twitch.user.id &&
			message.channel_id != sAuth.youtube?.user.id;
		//Don't save messages from remote channels
		if (isFromRemoteChan) return Promise.resolve();

		// ignore any pending update about this message
		const pending = this._pendingUpdates.get(message.id);
		if (pending) {
			this._pendingUpdates.delete(message.id);
			clearTimeout(pending.timeout);
			pending.resolvers.forEach((resolve) => resolve());
		}

		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.MESSAGES_TABLE, "readwrite")
				.objectStore(Database.MESSAGES_TABLE)
				.index("id")
				.openCursor(IDBKeyRange.only(message.id))
				.addEventListener("success", (event) => {
					const pointer = (event.target as IDBRequest).result;
					if (pointer) {
						pointer.delete().addEventListener("success", () => {
							resolve();
						});
					} else {
						resolve();
					}
				});
		});
	}

	/**
	 * Clears messages database content
	 */
	public async clearMessages(): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();

		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.MESSAGES_TABLE, "readwrite")
				.objectStore(Database.MESSAGES_TABLE)
				.clear()
				.addEventListener("success", async (_event) => {
					console.log("[IndexedDB] message history cleared");
					resolve();
				});
		});
	}

	/**
	 * Clears groq database content
	 */
	public async clearGroqHistory(): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();

		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
				.objectStore(Database.GROQ_HISTORY_TABLE)
				.clear()
				.addEventListener("success", async (_event) => {
					console.log("[IndexedDB] groq history cleared");
					resolve();
				});
		});
	}

	/**
	 * Get all Groq history items
	 */
	public async getGroqHistoryList(): Promise<TwitchatDataTypes.GroqHistoryItem[]> {
		if (!this._db || !this._ready) return [];
		return new Promise((resolve, _reject) => {
			const query = this._db
				.transaction(Database.GROQ_HISTORY_TABLE, "readonly")
				.objectStore(Database.GROQ_HISTORY_TABLE)
				.getAll();
			query.addEventListener("success", (event) => {
				const result =
					((event.target as IDBRequest).result as TwitchatDataTypes.GroqHistoryItem[]) ||
					[];
				resolve(result);
			});
			query.addEventListener("error", (event) => {
				console.error("Get Groq history list error");
				console.error(event);
				resolve([]);
			});
		});
	}

	/**
	 * Add a Grod history item to DB
	 *
	 * @param message
	 */
	public async addGroqHistory(data: TwitchatDataTypes.GroqHistoryItem): Promise<void> {
		if (!this._db || !this._ready) return Promise.reject("Database not ready");
		data = toRaw(data);

		return new Promise((resolve, reject) => {
			const query = this._db
				.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
				.objectStore(Database.GROQ_HISTORY_TABLE)
				.add(data);
			query.addEventListener("success", (event) => {
				clearTimeout(this._cleanTimeout);
				this._cleanTimeout = window.setTimeout(() => {
					if ((event.target as IDBRequest).result > this._maxMessageRecords) {
						this.limitGroqCount();
					}
				}, 1000);
				resolve();
			});
			query.addEventListener("error", (event) => {
				console.error("Add groq history error");
				console.error(event);
				reject("[Database] Add groq history error");
			});
			query.addEventListener("onabort", (event) => {
				const error = (event.target as IDBRequest).error;
				if (error && error.name == "QuotaExceededError" && !this._quotaWarned) {
					this._quotaWarned = true;
					StoreProxy.common.alert(
						"[IndexedDB] Storage quota reached, cannot save Groq answer to history",
					);
					this.limitGroqCount();
				}
				resolve();
			});
		});
	}

	/**
	 * Updates a Groq history item
	 * @param entry
	 */
	public async updateGroqHistory(entry: TwitchatDataTypes.GroqHistoryItem): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();
		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
				.objectStore(Database.GROQ_HISTORY_TABLE)
				.index("id")
				.openCursor(IDBKeyRange.only(entry.id))
				.addEventListener("success", (event) => {
					const pointer = (event.target as IDBRequest).result;
					if (pointer) {
						pointer
							.update(JSON.parse(JSON.stringify(entry)))
							.addEventListener("success", () => {
								resolve();
							});
					} else {
						resolve();
					}
				});
		});
	}

	/**
	 * Deletes a Groq history item from DB
	 * @param message
	 */
	public async deleteGroqHistory(id: string): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();

		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
				.objectStore(Database.GROQ_HISTORY_TABLE)
				.index("id")
				.openCursor(IDBKeyRange.only(id))
				.addEventListener("success", (event) => {
					const pointer = (event.target as IDBRequest).result;
					if (pointer) {
						pointer.delete().addEventListener("success", () => {
							resolve();
						});
					} else {
						resolve();
					}
				});
		});
	}

	/**
	 * Get the number of emoji shortcode records in the DB
	 */
	public async getEmojiShortcodesCount(): Promise<number> {
		if (!this._db || !this._ready) return 0;
		return new Promise((resolve) => {
			const query = this._db
				.transaction(Database.EMOJI_SHORTCODES_TABLE, "readonly")
				.objectStore(Database.EMOJI_SHORTCODES_TABLE)
				.count();
			query.addEventListener("success", (event) => {
				resolve((event.target as IDBRequest).result as number);
			});
			query.addEventListener("error", () => {
				resolve(0);
			});
		});
	}

	/**
	 * Populate emoji shortcodes table with entries
	 */
	public async populateEmojiShortcodes(entries: EmojiShortcodeEntry[]): Promise<void> {
		if (!this._db || !this._ready) return;
		return new Promise((resolve, reject) => {
			const tx = this._db.transaction(Database.EMOJI_SHORTCODES_TABLE, "readwrite");
			const store = tx.objectStore(Database.EMOJI_SHORTCODES_TABLE);
			for (const entry of entries) {
				store.add(entry);
			}
			tx.addEventListener("complete", () => resolve());
			tx.addEventListener("error", () => reject("Failed to populate emoji shortcodes"));
		});
	}

	/**
	 * Loads emoji shortcodes from the static JSON file into IndexedDB.
	 * Skips if data is already populated.
	 */
	public async loadEmojiShortcodes(): Promise<void> {
		const count = await this.getEmojiShortcodesCount();
		if (count > 0) {
			const existing = await this.getAllEmojiShortcodes(1);
			if (existing.length > 0 && existing[0]?.codepoint) return;
			await this.clearEmojiShortcodes();
		}

		const result = await fetch("/emoji_shortcodes.json");
		const json = (await result.json()) as Record<string, string | string[]>;

		const entries: EmojiShortcodeEntry[] = [];
		for (const [codepoint, shortcodes] of Object.entries(json)) {
			const emoji = codepoint
				.split("-")
				.map((cp) => String.fromCodePoint(parseInt(cp, 16)))
				.join("");
			const normalizedCodepoint = codepoint.toLowerCase();
			if (typeof shortcodes === "string") {
				entries.push({ shortcode: shortcodes, emoji, codepoint: normalizedCodepoint });
			} else if (Array.isArray(shortcodes)) {
				for (const sc of shortcodes) {
					entries.push({ shortcode: sc, emoji, codepoint: normalizedCodepoint });
				}
			}
		}

		await this.populateEmojiShortcodes(entries);
	}

	/**
	 * Get all emoji shortcodes from the DB
	 */
	public async getAllEmojiShortcodes(limit = 0): Promise<EmojiShortcodeEntry[]> {
		if (!this._db || !this._ready) return [];
		return new Promise((resolve) => {
			const store = this._db
				.transaction(Database.EMOJI_SHORTCODES_TABLE, "readonly")
				.objectStore(Database.EMOJI_SHORTCODES_TABLE);
			const query = limit > 0 ? store.getAll(undefined, limit) : store.getAll();
			query.addEventListener("success", (event) => {
				resolve(((event.target as IDBRequest).result as EmojiShortcodeEntry[]) || []);
			});
			query.addEventListener("error", () => {
				resolve([]);
			});
		});
	}

	/**
	 * Search emoji shortcodes by query (contains match).
	 * Returns prefix matches first, then contains matches.
	 */
	public async searchEmojiShortcodes(
		query: string,
		limit: number = 50,
	): Promise<EmojiShortcodeEntry[]> {
		if (!this._db || !this._ready) return [];
		return new Promise((resolve) => {
			let exactResult: EmojiShortcodeEntry | null = null;
			const prefixResults: EmojiShortcodeEntry[] = [];
			const containsResults: EmojiShortcodeEntry[] = [];
			const store = this._db
				.transaction(Database.EMOJI_SHORTCODES_TABLE, "readonly")
				.objectStore(Database.EMOJI_SHORTCODES_TABLE);
			const request = store.openCursor();
			request.addEventListener("success", (event) => {
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const value = cursor.value as EmojiShortcodeEntry;
					if (value.shortcode === query) {
						exactResult = value;
					} else if (value.shortcode.startsWith(query)) {
						prefixResults.push(value);
					} else if (value.shortcode.indexOf(query) > -1) {
						containsResults.push(value);
					}
					cursor.continue();
				} else {
					const results: { shortcode: string; emoji: string }[] = [];
					if (exactResult) results.push(exactResult);
					results.push(...prefixResults, ...containsResults);
					resolve(results.slice(0, limit));
				}
			});
			request.addEventListener("error", () => {
				resolve([]);
			});
		});
	}

	public async clearEmojiShortcodes(): Promise<void> {
		if (!this._db || !this._ready) return Promise.resolve();

		return new Promise((resolve, _reject) => {
			this._db
				.transaction(Database.EMOJI_SHORTCODES_TABLE, "readwrite")
				.objectStore(Database.EMOJI_SHORTCODES_TABLE)
				.clear()
				.addEventListener("success", async (_event) => {
					resolve();
				});
		});
	}

	/*******************
	 * PRIVATE METHODS *
	 *******************/
	private limitMessageCount(): void {
		this._db
			.transaction(Database.MESSAGES_TABLE, "readwrite")
			.objectStore(Database.MESSAGES_TABLE)
			.count()
			.addEventListener("success", async (event) => {
				const entry = event.target as IDBRequest;
				const deleteCount = (entry.result as number) - this._maxMessageRecords;
				if (deleteCount > 0) {
					for (let i = 0; i < deleteCount; i++) {
						const store = this._db
							.transaction(Database.MESSAGES_TABLE, "readwrite")
							.objectStore(Database.MESSAGES_TABLE);
						store.openKeyCursor().addEventListener("success", (event) => {
							const pointer = event.target as IDBRequest | null;
							if (pointer) {
								store.delete(pointer.result.key);
								this._quotaWarned = false;
							}
						});
					}
				}
			});
	}

	private limitGroqCount(): void {
		this._db
			.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
			.objectStore(Database.GROQ_HISTORY_TABLE)
			.count()
			.addEventListener("success", async (event) => {
				const entry = event.target as IDBRequest;
				const deleteCount = (entry.result as number) - this._maxGroqRecords;
				if (deleteCount > 0) {
					for (let i = 0; i < deleteCount; i++) {
						const store = this._db
							.transaction(Database.GROQ_HISTORY_TABLE, "readwrite")
							.objectStore(Database.GROQ_HISTORY_TABLE);
						store.openKeyCursor().addEventListener("success", (event) => {
							const pointer = event.target as IDBRequest | null;
							if (pointer) {
								store.delete(pointer.result.key);
								this._quotaWarned = false;
							}
						});
					}
				}
			});
	}

	/**
	 * Execute a pending save
	 */
	private flushMessageUpdate(messageId: string): void {
		const pending = this._pendingUpdates.get(messageId);
		if (!pending) return;
		this._pendingUpdates.delete(messageId);
		clearTimeout(pending.timeout);

		const done = () => pending.resolvers.forEach((resolve) => resolve());

		if (!this._db || !this._ready) {
			done();
			return;
		}

		this._db
			.transaction(Database.MESSAGES_TABLE, "readwrite")
			.objectStore(Database.MESSAGES_TABLE)
			.index("id")
			.openCursor(IDBKeyRange.only(messageId))
			.addEventListener("success", (event) => {
				const pointer = (event.target as IDBRequest).result;
				if (pointer) {
					const { data } = this.removeCircularReferences(pending.message);
					pointer.update(data).addEventListener("success", () => {
						done();
					});
				} else {
					//Message no longer on the DB (deleted meanwhile), nothing to update
					done();
				}
			});
	}

	/**
	 * Remove some props known for being potential source of circular references
	 * @param message
	 */
	private removeCircularReferences<T extends TwitchatDataTypes.ChatMessageTypes>(
		message: T,
	): { data: T; json: string } {
		type KeysOfUnion<T> = T extends T ? keyof T : never;
		type keys = KeysOfUnion<TwitchatDataTypes.ChatMessageTypes>;

		message = toRaw(message);

		let rootMessage =
			message.type === TwitchatDataTypes.TwitchatMessageType.MESSAGE ? message : undefined;

		if (!rootMessage) {
			// Find the first instance of message type that is "MESSAGE" in message object and its sub-properties
			// This won't work properly if multiple props referrence message instances at the root level, only
			// the first found will be considered the root message
			const visited = new WeakSet<object>();
			const findMessageType = (obj: any): T | undefined => {
				if (obj && typeof obj === "object") {
					// Don't walk the same object twice. Also protects against
					// circular references which would blow the stack up here
					if (visited.has(obj)) return undefined;
					visited.add(obj);
					// Check if current object has type property with MESSAGE value
					if (obj.type === TwitchatDataTypes.TwitchatMessageType.MESSAGE) {
						return obj;
					}
					// this skips user instances that cannot hold a messge instance.
					// save quite a bunch of loops on messages referencing lots of users (e.g. subgifts)
					if ("channelInfo" in obj) return undefined;
					// Recursively check all properties
					for (const key in obj) {
						const result = findMessageType(obj[key]);
						if (result) return result;
					}
				}
				return undefined;
			};

			rootMessage = findMessageType(message);
		}

		let json = "";
		try {
			json = JSON.stringify(message, function (key, value) {
				// Cleanup circular references induced by chat message referencing
				// each other circularly through answers/answersTo/directlyAnswersTo props
				// But only do this for messages that are not the root message itself
				if (this === rootMessage) return value;

				const typedKey = key as keys;
				if (typedKey == "answers") return [];
				if (typedKey == "answersTo") return undefined;
				if (typedKey == "directlyAnswersTo") return undefined;

				return value;
			});
		} catch (error) {
			const log =
				"Failed stringifying message for DB storage for message type " + message.type;
			console.error(log, error);
			Sentry.captureException(log);
		}
		const clone = JSON.parse(json);

		return { data: clone, json };
	}

	private createMessageTable(): void {
		const tableList = this._db.objectStoreNames;
		// Create MESSAGES_TABLE if it doesn't exist
		if (!tableList.contains(Database.MESSAGES_TABLE)) {
			this._messageStore = this._db.createObjectStore(Database.MESSAGES_TABLE, {
				autoIncrement: true,
			});
			this._messageStore.createIndex("id", "id", { unique: true });
			this._messageStore.transaction.oncomplete = (event) => {
				console.log("MESSAGES_TABLE created", event);
			};
			this._messageStore.transaction.onerror = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage("Message table creation error. DB version: " + version);
			};
			this._messageStore.transaction.onabort = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage("Message table creation aborted. DB version: " + version);
			};
		}
	}

	private createEmojiShortcodesTable(): void {
		const tableList = this._db.objectStoreNames;
		if (!tableList.contains(Database.EMOJI_SHORTCODES_TABLE)) {
			const store = this._db.createObjectStore(Database.EMOJI_SHORTCODES_TABLE, {
				autoIncrement: true,
			});
			store.createIndex("shortcode", "shortcode", { unique: true });
			store.transaction.oncomplete = (event) => {
				console.log("EMOJI_SHORTCODES_TABLE created", event);
			};
			store.transaction.onerror = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage(
					"Emoji shortcodes table creation error. DB version: " + version,
				);
			};
			store.transaction.onabort = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage(
					"Emoji shortcodes table creation aborted. DB version: " + version,
				);
			};
		}
	}

	private createGroqTable(): void {
		const tableList = this._db.objectStoreNames;
		// Create GROQ_HISTORY_TABLE if it doesn't exist
		if (!tableList.contains(Database.GROQ_HISTORY_TABLE)) {
			this._groqStore = this._db.createObjectStore(Database.GROQ_HISTORY_TABLE, {
				autoIncrement: true,
			});
			this._groqStore.createIndex("id", "id", { unique: true });
			this._groqStore.transaction.oncomplete = (event) => {
				console.log("GROQ_HISTORY_TABLE created", event);
			};
			this._groqStore.transaction.onerror = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage("Groq table creation error. DB version: " + version);
			};
			this._groqStore.transaction.onabort = (event) => {
				const version = this._db.version;
				Sentry.captureException(event);
				Sentry.captureMessage("Groq table creation aborted. DB version: " + version);
			};
		}
	}
}

interface PendingMessageUpdate {
	message: TwitchatDataTypes.ChatMessageTypes;
	resolvers: (() => void)[];
	/** Date of the first update request of the current debounce window */
	firstRequestDate: number;
	timeout: number;
}
